import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import MetaData from './layouts/MetaData';
import FeaturedProducts from './product/FeaturedProducts';

const Home = () => {
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [failedBanners, setFailedBanners] = useState(new Set());

    // Fixed banners array - corrected the third banner URL with proper filename
    const banners = [
        "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-premium-meat-seller-bengaluru-city-3.png.png",
        "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-premium-meat-seller-bengaluru-city-1.png",
        "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-premium-meat-seller-bengaluru-city-2.png",
        // Fixed: Added correct filename with double .png extension as it exists in S3
        
    ];

    useEffect(() => {
        // Banner rotation interval
        const bannerInterval = setInterval(() => {
            setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
        }, 5000);

        // Window resize handler
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);

        // Cleanup function
        return () => {
            clearInterval(bannerInterval);
            window.removeEventListener('resize', handleResize);
        };
    }, [banners.length]);

    const nextBanner = () => {
        setCurrentBannerIndex((prev) => (prev + 1) % banners.length);
    };

    const prevBanner = () => {
        setCurrentBannerIndex((prev) => (prev - 1 + banners.length) % banners.length);
    };

    // Handle banner image loading errors
    const handleImageError = (index, bannerUrl) => {
        console.error(`Banner ${index + 1} failed to load:`, bannerUrl);
        setFailedBanners(prev => new Set([...prev, index]));
    };

    // Process steps data
    const processSteps = [
        {
            title: "Step 1: Sourcing from Organic & Hygienic Farms",
            image: "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-1.png",
            description: "We partner with <strong>certified farms</strong> that follow <strong>ethical and organic</strong> farming practices. Our livestock is <strong>grass-fed</strong>, well-nurtured, and raised in a <strong>stress-free</strong> environment, ensuring <strong>tender and flavorful meat</strong>.",
            alt: "Organic Farms"
        },
        {
            title: "Step 2: Quality Checks by Experts",
            image: "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/quality-checks-by-experts-meat-buddy-bengaluru-meat-seller.png",
            description: "Before processing, each batch undergoes <strong>rigorous quality checks</strong> by our <strong>expert veterinarians</strong> and meat specialists. We ensure:<br />✔ No antibiotics or harmful chemicals<br />✔ Proper health certifications<br />✔ Adherence to <strong>FSSAI</strong> and hygiene standards",
            alt: "Quality Checks"
        },
        {
            title: "Step 3: Cold Chain Processing (0-4°C Storage)",
            image: "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/cold-chain-processing-0-4c-storage.png",
            description: "To maintain <strong>freshness and nutritional value</strong>, our meat is handled in a <strong>temperature-controlled</strong> environment from the farm to your home.<br />✔ <strong>Chilled Processing & Storage (0-4°C)</strong> -- Prevents bacterial growth<br />✔ Maintains <strong>juiciness, taste, and texture</strong><br />✔ No frozen storage -- <strong>Always fresh, never frozen</strong>",
            alt: "Cold Chain Processing"
        },
        {
            title: "Step 4: Clean & Hygienic Butchering",
            image: "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/clean-hygienic-butchering.png",
            description: "Our state-of-the-art butchering process follows <strong>strict hygiene protocols</strong> to maintain the <strong>highest food safety standards</strong>:<br />✔ <strong>Sterilized equipment & sanitized workstations</strong><br />✔ <strong>Halal & hygienic slaughtering techniques</strong><br />✔ Skilled butchers ensure <strong>precise cuts & minimal waste</strong>",
            alt: "Hygienic Butchering"
        },
        {
            title: "Step 5: Fresh Meat Packaging",
            image: "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/fresh-meat-packaging.png",
            description: "We use <strong>vacuum-sealed & eco-friendly packaging</strong> that locks in <strong>freshness, taste, and nutrition</strong>.<br />✔ <strong>Leak-proof & tamper-proof packaging</strong><br />✔ Maintains <strong>meat quality & hygiene</strong><br />✔ <strong>No preservatives, 100% natural</strong>",
            alt: "Fresh Meat Packaging"
        },
        {
            title: "Step 6: Fast & Reliable Home Delivery",
            image: "https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-6.png",
            description: "Your order is delivered <strong>within hours</strong> of processing, ensuring you get the <strong>freshest meat</strong> straight to your doorstep.<br />✔ <strong>Cold chain-enabled delivery</strong> -- Meat stays fresh until it reaches you<br />✔ <strong>Hassle-free online ordering</strong><br />✔ <strong>Safe, contactless delivery</strong>",
            alt: "Home Delivery"
        }
    ];

    return (
        <>
            <MetaData title="Fresh Meat Delivered To Your Doorstep" />
            
            {/* Main container with background image */}
            <div
                className="min-h-screen"
                style={{
                    backgroundImage: `url('https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png')`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                {/* Banner Slider with Link to Shop */}
                <div className="relative">
                    <Link to="/shop" className="block">
                        <div className="relative overflow-hidden">
                            <div className="relative">
                                {banners.map((banner, index) => (
                                    <div
                                        key={index}
                                        className={`transition-opacity duration-500 ${index === currentBannerIndex ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                                    >
                                        <img
                                            src={banner}
                                            alt={`Banner ${index + 1}`}
                                            className="w-full h-auto object-cover"
                                            onError={() => handleImageError(index, banner)}
                                            onLoad={() => console.log(`Banner ${index + 1} loaded successfully`)}
                                        />
                                        {/* Show error message if banner fails to load */}
                                        {failedBanners.has(index) && (
                                            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
                                                <p className="text-gray-500 text-center p-4">
                                                    Banner {index + 1} failed to load<br />
                                                    <small>{banner}</small>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            
                            {/* Banner Navigation Buttons */}
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    prevBanner();
                                }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 transition-all z-10"
                                aria-label="Previous banner"
                            >
                                <ArrowLeft className="w-6 h-6" />
                            </button>
                            
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    nextBanner();
                                }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-70 transition-all z-10"
                                aria-label="Next banner"
                            >
                                <ArrowRight className="w-6 h-6" />
                            </button>
                            
                            {/* Banner Indicators */}
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                                {banners.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setCurrentBannerIndex(index);
                                        }}
                                        className={`w-3 h-3 rounded-full ${index === currentBannerIndex ? 'bg-white' : 'bg-white bg-opacity-50'}`}
                                        aria-label={`Go to banner ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </Link>
                </div>
                
                {/* Add spacing between banner and Featured Products section */}
                <div className="pt-4 md:pt-6"></div>
                
                {/* Featured Products Section with adjusted sizing for mobile */}
                <div className="px-4 md:px-6">
                    {/* Featured Products Component */}
                    <FeaturedProducts />
                </div>
                
                {/* Quality Process Section */}
                <div className="py-10 px-4 md:px-12">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-8 bg-opacity-80 rounded-lg p-4">
                            <h4 className="text-xl md:text-2xl font-bold text-red-600 mb-3 font-['Crimson_Text']">
                                From Farm to Your Plate - Our Quality Process
                            </h4>
                            <p className="text-sm text-gray-700 max-w-3xl mx-auto text-justify px-4">
                                At <strong>Meatbuddy</strong>, we ensure that every bite you take is fresh, safe, and full of nutrition.
                                Our <strong>farm-to-plate process</strong> is designed to maintain the highest standards of hygiene,
                                quality, and freshness. Here's how we bring <strong>premium meat</strong> straight to your doorstep:
                            </p>
                        </div>
                        
                        {/* Desktop Process Steps - Improved spacing and alignment */}
                        {!isMobile && (
                            <div className="mb-10 space-y-3 px-4 md:px-8">
                                {processSteps.map((step, index) => (
                                    <div key={index} className="flex items-center px-6 py-3 rounded-lg bg-white bg-opacity-75 shadow-sm">
                                        <div className="h-40 w-40 rounded-full overflow-hidden border-4 border-red-600 flex-shrink-0 mr-8">
                                            <img
                                                src={step.image}
                                                alt={step.alt}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1 px-6">
                                            <h4 className="text-lg font-bold text-red-600 mb-2">
                                                {step.title}
                                            </h4>
                                            <p className="text-sm text-gray-700 text-justify max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: step.description }}></p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* Mobile Process Steps */}
                        {isMobile && (
                            <div className="mb-10 relative px-4">
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-lg font-bold text-red-600">Our Quality Process</h3>
                                </div>
                                
                                {/* Mobile steps with pagination */}
                                <div className="relative rounded-lg p-3 bg-white bg-opacity-75 shadow-sm">
                                    {processSteps.map((step, index) => (
                                        <div
                                            key={index}
                                            className={`transition-opacity duration-300 ${
                                                index === currentStepIndex ? 'block opacity-100' : 'hidden opacity-0'
                                            }`}
                                        >
                                            <div className="flex flex-col items-center">
                                                <h4 className="text-lg font-bold text-red-600 mb-2 text-center">
                                                    {step.title}
                                                </h4>
                                                
                                                <div className="h-36 w-36 rounded-full overflow-hidden border-4 border-red-600 mb-2">
                                                    <img
                                                        src={step.image}
                                                        alt={step.alt}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                
                                                <div className="w-full px-6 pb-2">
                                                    <p className="text-sm text-gray-700 text-justify mx-auto"
                                                       dangerouslySetInnerHTML={{ __html: step.description }}>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Navigation controls */}
                                    <div className="flex justify-between mt-4">
                                        <button
                                            onClick={() => setCurrentStepIndex(prev => (prev - 1 + processSteps.length) % processSteps.length)}
                                            className="p-2 rounded-full shadow-md bg-red-600 text-white hover:bg-red-700 transition-colors"
                                            aria-label="Previous step"
                                        >
                                            <ArrowLeft className="w-5 h-5" />
                                        </button>
                                        
                                        <div className="flex space-x-2 items-center">
                                            {processSteps.map((_, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => setCurrentStepIndex(idx)}
                                                    className={`w-2.5 h-2.5 rounded-full ${
                                                        idx === currentStepIndex ? 'bg-red-600' : 'bg-gray-400'
                                                    }`}
                                                    aria-label={`Go to step ${idx + 1}`}
                                                />
                                            ))}
                                        </div>
                                        
                                        <button
                                            onClick={() => setCurrentStepIndex(prev => (prev + 1) % processSteps.length)}
                                            className="p-2 rounded-full shadow-md bg-red-600 text-white hover:bg-red-700 transition-colors"
                                            aria-label="Next step"
                                        >
                                            <ArrowRight className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Why Choose Meatbuddy section */}
                        <div className="rounded-xl shadow-lg p-6 mb-8 bg-white">
                            <h3 className="text-xl font-bold text-red-600 mb-4 text-center">
                                Why Choose Meatbuddy?
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="flex items-start gap-2">
                                    <span className="text-green-500 text-base">✅</span>
                                    <p className="text-sm text-gray-700"><strong>Farm-Fresh & Antibiotic-Free</strong> Meat</p>
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className="text-green-500 text-base">✅</span>
                                    <p className="text-sm text-gray-700"><strong>Ethically Sourced & Hygienically Processed</strong></p>
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className="text-green-500 text-base">✅</span>
                                    <p className="text-sm text-gray-700"><strong>No Frozen Storage -- Always Fresh</strong></p>
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className="text-green-500 text-base">✅</span>
                                    <p className="text-sm text-gray-700"><strong>FSSAI Certified & 100% Safe</strong></p>
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className="text-green-500 text-base">✅</span>
                                    <p className="text-sm text-gray-700"><strong>Fast & Reliable Delivery</strong></p>
                                </div>
                            </div>
                            <div className="mt-4 text-center">
                                <Link
                                    to="/shop"
                                    className="inline-block px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors no-underline text-sm"
                                >
                                    🔗 Order Now & Experience the Freshness!
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Home;